// Template management extracted from OfferPageBuilderSimple.js lines 2780-3620
import { useCallback, useEffect } from 'react';
import { API_URL } from '../../../config';
import { getPreBuiltTemplates, getAllTemplatesByType } from '../../../services/offerTemplateService';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';

// Helper function to filter out unwanted templates
export const filterLegitimateTemplates = (templates) => {
  if (!templates || !Array.isArray(templates)) return [];

  return templates.filter(template => {
    // Filter out converted templates only
    if (template.description && template.description.toLowerCase().includes('converted')) {
      console.log(`🗑️  Filtering out converted template: ${template.name}`);
      return false;
    }

    return true;
  });
};

// Helper function to group templates by type and subtype
export const groupTemplatesByType = (templates) => {
  if (!templates || !Array.isArray(templates)) return {};

  console.log('🔍 Grouping templates:', templates.map(t => ({ name: t.name, type: t.type, subtype: t.subtype })));

  const grouped = templates.reduce((acc, template) => {
    const type = template.type || 'Other';
    const subtype = template.subtype || 'Other';

    console.log(`📁 Processing template: ${template.name} with type: "${type}" and subtype: "${subtype}"`);

    if (!acc[type]) {
      acc[type] = {};
    }
    if (!acc[type][subtype]) {
      acc[type][subtype] = [];
    }
    acc[type][subtype].push(template);
    return acc;
  }, {});

  console.log('📊 Grouped templates:', grouped);

  // Sort types alphabetically and sort subtypes and templates within each
  const sortedGrouped = {};
  Object.keys(grouped).sort().forEach(type => {
    sortedGrouped[type] = {};
    Object.keys(grouped[type]).sort().forEach(subtype => {
      sortedGrouped[type][subtype] = grouped[type][subtype].sort((a, b) => a.name.localeCompare(b.name));
    });
  });

  console.log('🎯 Final sorted grouped templates:', sortedGrouped);
  return sortedGrouped;
};

export const useTemplates = ({
  elements,
  pages,
  setPages,
  setElements,
  pageSettings,
  setPageSettings,
  templateName,
  setTemplateName,
  templateType,
  setTemplateType,
  templateSubtype,
  setTemplateSubtype,
  currentTemplateId,
  setCurrentTemplateId,
  savedTemplates,
  setSavedTemplates,
  preBuiltTemplates,
  setPreBuiltTemplates,
  buttonImageCalloutTemplates,
  setButtonImageCalloutTemplates,
  buttonNoticeTemplates,
  setButtonNoticeTemplates,
  buttonTextTemplates,
  setButtonTextTemplates,
  showTemplatesList,
  setShowTemplatesList,
  setShowSaveDialog,
  setStatusTitle,
  setStatusMessage,
  setShowStatusAlert,
  setConfirmDialogTitle,
  setConfirmDialogMessage,
  setConfirmAction,
  setShowConfirmDialog,
  setSelectedElement,
  setSelectedTemplateId,
  setSelectedVariantKey,
  setSelectedSavedElementId,
  setCurrentElementName,
  setIsUpdatingElement,
  setCurrentPageIndex,
  setNextPageId,
  setPaginationEnabled,
  filteredTemplates,
  setFilteredTemplates,
  selectedCustomCategory,
  selectedCustomSubtype,
  paginationEnabled,
  currentPageIndex,
  setForceRender,
  localElements,
  setLocalElements,
  isBrandsPage,
  setSharedElements,
  sharedElements,
  sharedPages,
  setSharedPages,
  selectedOffer,
  offers,
  populateOfferLinkInElements,
  customElementsOnly,
}) => {
  // Template management functions
  const loadTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Loading templates with token:', token ? 'Token exists' : 'No token found');

      // Determine which endpoint to use based on user role
      let isAdmin = false;
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        isAdmin = tokenPayload.role === 'admin';
        console.log('User role from token:', tokenPayload.role, 'isAdmin:', isAdmin);
      } catch (e) {
        console.log('Could not decode token, defaulting to user endpoint');
      }

      const endpoint = isAdmin
        ? `${API_URL}/offer-templates/admin/all-templates?limit=100`
        : `${API_URL}/offer-templates/user/my-templates?limit=100`;

      console.log('Using endpoint:', endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Load templates response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Templates loaded:', data);

        // Filter out unwanted templates
        const filtered = filterLegitimateTemplates(data.templates);
        console.log(`📊 Filtered templates: ${data.templates?.length || 0} → ${filtered.length}`);

        // Create deep copies to prevent reference sharing
        const templatesCopy = filtered.map(template => ({
          ...template,
          elements: template.elements ? JSON.parse(JSON.stringify(template.elements)) : [],
          pageSettings: template.pageSettings ? JSON.parse(JSON.stringify(template.pageSettings)) : null
        })) || [];

        setSavedTemplates(templatesCopy);
      } else {
        console.error('Failed to load templates - Status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, []);

  const saveTemplate = useCallback(async () => {
    if (!templateName.trim()) {
      setStatusTitle('Missing Information');
      setStatusMessage('Please enter a template name');
      setShowStatusAlert(true);
      return;
    }

    if (!templateType) {
      setStatusTitle('Missing Information');
      setStatusMessage('Please select a template type');
      setShowStatusAlert(true);
      return;
    }

    if (!templateSubtype) {
      setStatusTitle('Missing Information');
      setStatusMessage('Please select a template subtype');
      setShowStatusAlert(true);
      return;
    }

    try {
      const templateData = {
        name: templateName,
        description: `Page builder template with ${elements.length} elements`,
        category: 'product', // Default category
        type: templateType,
        subtype: templateSubtype,
        elements: elements,
        pageSettings: pageSettings,
        template: {
          title: pageSettings.title || 'My Offer Page',
          description: `Page builder template with ${elements.length} elements`,
          callToAction: 'Get This Deal',
          urgencyText: 'Limited Time Offer',
          validityDays: 7
        }
      };

      console.log('Saving template with data:', templateData);

      const token = localStorage.getItem('token');
      console.log('Using token:', token ? 'Token exists' : 'No token found');

      const isUpdate = currentTemplateId !== null;
      const url = isUpdate ? `${API_URL}/offer-templates/${currentTemplateId}` : `${API_URL}/offer-templates`;
      const method = isUpdate ? 'PUT' : 'POST';

      console.log(`${isUpdate ? 'Updating' : 'Creating'} template with method: ${method}, URL: ${url}`);

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const savedTemplate = await response.json();

        if (isUpdate) {
          // Update existing template in the list
          setSavedTemplates(prev => prev.map(t =>
            t._id === currentTemplateId ? savedTemplate : t
          ));
          setStatusTitle('Success');
          setStatusMessage('Template updated successfully!');
        } else {
          // Add new template to the list
          setSavedTemplates(prev => [...prev, savedTemplate]);
          setCurrentTemplateId(savedTemplate._id); // Now tracking this template
          setStatusTitle('Success');
          setStatusMessage('Template saved successfully!');
        }
        setShowStatusAlert(true);

        setShowSaveDialog(false);
      } else {
        console.error('Save template error - Status:', response.status);
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || 'Unknown error';
          console.error('Save template error - Data:', errorData);
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        setStatusTitle('Error');
        setStatusMessage(`Failed to ${isUpdate ? 'update' : 'save'} template: ${errorMessage}`);
        setShowStatusAlert(true);
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      setStatusTitle('Error');
      setStatusMessage('Failed to save template');
      setShowStatusAlert(true);
    }
  }, [templateName, templateType, templateSubtype, elements, pageSettings, currentTemplateId]);

  const loadTemplate = useCallback(async (templateId) => {
    try {
      // Clear any existing state to prevent pollution
      setSelectedElement(null);
      setSelectedTemplateId(null);
      setSelectedVariantKey(null);

      // CRITICAL FIX: Immediately clear pages state to prevent old pages from persisting
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
      setNextPageId(2);

      const response = await fetch(`${API_URL}/offer-templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const template = await response.json();

        // Load elements and page settings if they exist (page builder templates)
        if (template.elements && Array.isArray(template.elements)) {
          console.log('🔄 Loading template elements:', template.elements.length, 'elements');
          setElements(template.elements);
          // Set as single page without auto-splitting
          setPages([{ id: 1, elements: template.elements }]);
          setCurrentPageIndex(0);
          setNextPageId(2);
          setPaginationEnabled(false);
          console.log('📄 Template loaded as single page without auto-split');
        } else {
          console.log('🔄 No template elements found, setting empty array');
          setElements([]);
        }

        // Reset update state when loading a template
        setSelectedSavedElementId(null);
        setCurrentElementName('');
        setIsUpdatingElement(false);

        if (template.pageSettings) {
          setPageSettings(template.pageSettings);
        } else {
          setPageSettings({
            title: template.template?.title || 'My Offer Page',
            backgroundColor: '#ffffff',
            maxWidth: '800px',
            fontFamily: 'Arial, sans-serif'
          });
        }

        // Track the currently loaded template
        setCurrentTemplateId(templateId);
        setTemplateName(template.name); // Pre-fill the name for updates
        setTemplateType(template.type || ''); // Pre-fill the type for updates
        setTemplateSubtype(template.subtype || ''); // Pre-fill the subtype for updates

        setShowTemplatesList(false);

        // Increment usage count
        fetch(`${API_URL}/offer-templates/${templateId}/apply`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).catch(err => console.log('Failed to update usage count:', err));

      } else {
        const errorData = await response.json();
        setStatusTitle('Error');
        setStatusMessage(`Failed to load template: ${errorData.message || 'Unknown error'}`);
        setShowStatusAlert(true);
      }
    } catch (error) {
      console.error('Failed to load template:', error);
      setStatusTitle('Error');
      setStatusMessage('Failed to load template');
      setShowStatusAlert(true);
    }
  }, []);

  const deleteTemplate = useCallback(async (templateId) => {
    setConfirmDialogTitle('Delete Template');
    setConfirmDialogMessage('Are you sure you want to delete this template?');
    setConfirmAction(() => async () => {
      try {
        const response = await fetch(`${API_URL}/offer-templates/${templateId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          setSavedTemplates(prev => prev.filter(t => t._id !== templateId));

          // If we just deleted the currently loaded template, clear the state
          if (templateId === currentTemplateId) {
            setCurrentTemplateId(null);
            setTemplateName('');
            setTemplateType('');
            setTemplateSubtype('');
          }

          setStatusTitle('Success');
          setStatusMessage('Template deleted successfully!');
          setShowStatusAlert(true);
        } else {
          const errorData = await response.json();
          setStatusTitle('Error');
          setStatusMessage(`Failed to delete template: ${errorData.message || 'Unknown error'}`);
          setShowStatusAlert(true);
        }
      } catch (error) {
        console.error('Failed to delete template:', error);
        setStatusTitle('Error');
        setStatusMessage('Failed to delete template');
        setShowStatusAlert(true);
      }
    });
    setShowConfirmDialog(true);
  }, [currentTemplateId]);

  // Create a new template (reset current template state)
  const createNewTemplate = useCallback(() => {
    setCurrentTemplateId(null);
    setTemplateName('');
    setTemplateType('');
    setTemplateSubtype('');
    setElements([]);
    setPageSettings({
      title: 'My Offer Page',
      backgroundColor: '#ffffff',
      maxWidth: '800px',
      fontFamily: 'Arial, sans-serif'
    });
    // Reset update state when creating new template
    setSelectedSavedElementId(null);
    setCurrentElementName('');
    setIsUpdatingElement(false);
  }, []);

  const loadPreBuiltTemplate = useCallback((templateData, forceNewPage = false) => {
    // Clear any existing state to prevent pollution
    setSelectedElement(null);

    // Create deep copies to prevent reference sharing
    let elementsCopy = templateData.elements ? JSON.parse(JSON.stringify(templateData.elements)) : [];
    const pageSettingsCopy = templateData.pageSettings ? JSON.parse(JSON.stringify(templateData.pageSettings)) : {
      title: 'My Offer Page',
      backgroundColor: '#ffffff',
      maxWidth: '800px',
      fontFamily: 'Arial, sans-serif'
    };

    // Auto-populate offer link if selected
    if (selectedOffer && offers.length > 0) {
      const selectedOfferObj = offers.find(o => o._id === selectedOffer);
      if (selectedOfferObj && selectedOfferObj.purchaseUrl && selectedOfferObj.purchaseUrl !== '#' && selectedOfferObj.purchaseUrl !== '') {
        console.log('🔗 Auto-populating offer link for prebuilt template:', selectedOfferObj.purchaseUrl);
        elementsCopy = populateOfferLinkInElements(elementsCopy, selectedOfferObj.purchaseUrl);
      }
    }

    console.log('🔍 Loading prebuilt template with deep copies:', {
      templateName: templateData.name,
      templateId: templateData._id,
      originalElements: templateData.elements ? templateData.elements.length : 0,
      copiedElements: elementsCopy.length,
      firstElementId: elementsCopy[0]?.id,
      type: templateData.type,
      subtype: templateData.subtype
    });

    // Define wasPaginationEnabled at top scope of this block
    const wasPaginationEnabled = paginationEnabled;
    const shouldAppend = (sharedPages || setSharedPages) && elementsCopy.length > 0;

    if (shouldAppend && setSharedPages) {
      console.log('📄 Shared pages detected. Determining if we should append or replace.');

      setSharedPages(currentPages => {
        // Ensure currentPages is an array
        const safePages = Array.isArray(currentPages) && currentPages.length > 0
          ? currentPages
          : [{ id: 1, elements: [] }];

        const lastPage = safePages[safePages.length - 1];
        const lastPageHasContent = lastPage && lastPage.elements && lastPage.elements.length > 0;

        console.log('📄 Inside setSharedPages update:', {
          currentPagesLength: safePages.length,
          lastPageId: lastPage.id,
          lastPageHasContent,
          forceNewPage
        });

        if (forceNewPage || lastPageHasContent) {
          // Special case: if this is the very first page and it's empty, populate it instead of creating page 2
          const isFirstPageEmpty = safePages.length === 1 && !lastPageHasContent;

          if (isFirstPageEmpty) {
            console.log('📄 First page is empty. Populating it instead of creating page 2.');
            const updatedPages = [...safePages];
            const updatedFirstPage = { ...updatedPages[0], elements: elementsCopy };
            updatedPages[0] = updatedFirstPage;

            setTimeout(() => {
              setPages(updatedPages);
              setCurrentPageIndex(0);
              setPaginationEnabled(true);
            }, 0);

            return updatedPages;
          } else {
            console.log('📄 Appending new page (forceNewPage:', forceNewPage, 'hasContent:', lastPageHasContent, ')');
            const maxId = safePages.reduce((max, p) => Math.max(max, p.id || 0), 0);
            const newPageId = maxId + 1;
            const newPage = { id: newPageId, elements: elementsCopy };
            const updatedPages = [...safePages, newPage];

            setTimeout(() => {
              setPages(updatedPages);
              setCurrentPageIndex(updatedPages.length - 1);
              setPaginationEnabled(true);
            }, 0);

            return updatedPages;
          }
        } else {
          console.log('📄 Current page is empty. Replacing content on current page.');
          const updatedPages = [...safePages];
          // Create a new object for the last page to ensure immutability
          const updatedLastPage = { ...updatedPages[updatedPages.length - 1], elements: elementsCopy };
          updatedPages[updatedPages.length - 1] = updatedLastPage;

          setTimeout(() => {
            setPages(updatedPages);
            setPaginationEnabled(true);
          }, 0);

          return updatedPages;
        }
      });
    } else {
      // Legacy behavior for single-page mode or non-shared pages
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
      setNextPageId(2);

      // Disable pagination completely during template load for legacy mode
      setPaginationEnabled(false);

      console.log('🔧 About to call setElements with:', elementsCopy.length, 'elements');

      // CRITICAL: Call setSharedElements DIRECTLY instead of through setElements
      if (isBrandsPage && setSharedElements) {
        console.log('🔧 Calling setSharedElements DIRECTLY for Brands page');
        setSharedElements([]);
        setTimeout(() => {
          setSharedElements(elementsCopy);
        }, 50);
      } else {
        console.log('🔧 Calling setLocalElements for non-Brands page');
        setElements(elementsCopy);
      }
    }
    console.log('✅ setElements called - waiting for state update');

    // Verify the update in next tick
    setTimeout(() => {
      console.log('🔍 After setElements - sharedElements:', sharedElements?.length || 0);
      console.log('🔍 After setElements - localElements:', localElements?.length || 0);
      console.log('🔍 After setElements - sharedElements IDs:', sharedElements?.map(e => e.id) || []);
    }, 10);

    // ONLY re-enable pagination if it was previously enabled
    if (wasPaginationEnabled) {
      // Set as single page without auto-splitting
      setPages([{ id: 1, elements: elementsCopy }]);
      setCurrentPageIndex(0);
      setNextPageId(2);
      setPaginationEnabled(true);
      console.log('📄 Template import completed as single page with pagination enabled');
    }

    setPageSettings(pageSettingsCopy);
    setShowTemplatesList(false);

    // Track the currently loaded template for updates
    setCurrentTemplateId(templateData._id);
    setTemplateName(templateData.name); // Pre-fill the name for updates
    setTemplateType(templateData.type || ''); // Pre-fill the type for updates
    setTemplateSubtype(templateData.subtype || ''); // Pre-fill the subtype for updates

    // Set selected template ID and clear variant selection
    setSelectedTemplateId(templateData._id);
    setSelectedVariantKey(null);

    // Reset update state when loading a prebuilt template
    setSelectedSavedElementId(null);
    setCurrentElementName('');
    setIsUpdatingElement(false);
  }, [
    paginationEnabled,
    localElements,
    setLocalElements,
    isBrandsPage,
    setSharedElements,
    sharedElements,
    sharedPages,
    setSharedPages,
    setShowTemplatesList,
    setPageSettings,
    setTemplateName,
    setTemplateType,
    setTemplateSubtype,
    setSelectedTemplateId,
    setSelectedVariantKey,
    setSelectedSavedElementId,
    setCurrentElementName,
    setIsUpdatingElement,
    setPages,
    setCurrentPageIndex,
    setNextPageId,
    setPaginationEnabled,
    setCurrentTemplateId,
    selectedOffer,
    offers,
    populateOfferLinkInElements
  ]);

  // Load pre-built templates from API
  const loadPreBuiltTemplates = useCallback(async () => {
    try {
      const data = await getPreBuiltTemplates();

      // Filter out unwanted templates
      const filtered = filterLegitimateTemplates(data.templates);
      console.log(`📊 Filtered prebuilt templates: ${data.templates?.length || 0} → ${filtered.length}`);

      // Create deep copies to prevent reference sharing
      const templatesCopy = filtered.map(template => ({
        ...template,
        elements: template.elements ? JSON.parse(JSON.stringify(template.elements)) : [],
        pageSettings: template.pageSettings ? JSON.parse(JSON.stringify(template.pageSettings)) : null
      })) || [];
      setPreBuiltTemplates(templatesCopy);
    } catch (error) {
      console.error('Failed to load pre-built templates:', error);
    }
  }, []);

  // Load all templates for popup using the same API as Brands page (no authentication issues)
  const loadAllTemplatesForPopup = useCallback(async () => {
    try {
      console.log('🔄 Loading all templates for popup using unified API...');

      // Get all templates from all main types
      const templateTypes = ['Buttons', 'Headline/Subheadline', 'Highlight block', 'Lists', 'Icons', 'Features', 'Testimonial', 'Comparison', 'FAQ', 'Images', 'Videos', 'Video with button overlay'];

      const allTemplatesPromises = templateTypes.map(type =>
        getAllTemplatesByType(type).catch(error => {
          console.warn(`Failed to load ${type} templates:`, error);
          return { templates: [] };
        })
      );

      const allResponses = await Promise.all(allTemplatesPromises);
      const allTemplates = allResponses.flatMap(response => response.templates || []);

      console.log(`📦 Loaded ${allTemplates.length} total templates from all types`);

      // Filter out unwanted templates
      const filtered = filterLegitimateTemplates(allTemplates);
      console.log(`📊 Filtered all templates: ${allTemplates.length} → ${filtered.length}`);

      // Create deep copies and separate by type
      const templatesCopy = filtered.map(template => ({
        ...template,
        elements: template.elements ? JSON.parse(JSON.stringify(template.elements)) : [],
        pageSettings: template.pageSettings ? JSON.parse(JSON.stringify(template.pageSettings)) : null
      }));

      // Separate into user templates and prebuilt templates
      const userTemplates = templatesCopy.filter(t => !t.isPreBuilt);
      const prebuiltTemplates = templatesCopy.filter(t => t.isPreBuilt);

      console.log(`📋 Separated templates: ${userTemplates.length} user, ${prebuiltTemplates.length} prebuilt`);

      // Set the state
      setSavedTemplates(userTemplates);
      setPreBuiltTemplates(prebuiltTemplates);

      console.log('✅ All templates loaded successfully for popup');
    } catch (error) {
      console.error('❌ Failed to load templates for popup:', error);
    }
  }, []);

  // Load templates by type and subtype - unified function (both prebuilt and user templates)
  const loadTemplatesByTypeAndSubtype = useCallback(async (type, subtype) => {
    try {
      console.log(`🔄 Loading ALL templates (prebuilt + user) for type: ${type}, subtype: ${subtype}`);
      const data = await getAllTemplatesByType(type, subtype);
      console.log(`📦 Received ${data.templates?.length || 0} templates for ${type}/${subtype}`);
      console.log('📋 Template names:', data.templates?.map(t => `${t.name} (${t.isPreBuilt ? 'prebuilt' : 'user'})`) || []);

      // Filter out unwanted templates
      const filtered = filterLegitimateTemplates(data.templates);
      console.log(`📊 Filtered ${type}/${subtype} templates: ${data.templates?.length || 0} → ${filtered.length}`);

      // IMPORTANT: Create deep copies to prevent reference sharing
      const templatesCopy = filtered.map(template => ({
        ...template,
        elements: template.elements ? JSON.parse(JSON.stringify(template.elements)) : [],
        pageSettings: template.pageSettings ? JSON.parse(JSON.stringify(template.pageSettings)) : null
      })) || [];

      return templatesCopy;
    } catch (error) {
      console.error(`Failed to load ${subtype} templates:`, error);
      return []; // Return empty array on error
    }
  }, []);

  // Load templates by type and subtype
  const loadTemplatesByType = useCallback(async (type, subtype = null) => {
    try {
      const data = await getAllTemplatesByType(type, subtype);

      // Filter out unwanted templates
      const filtered = filterLegitimateTemplates(data.templates);
      console.log(`📊 Filtered ${type}${subtype ? '/' + subtype : ''} templates: ${data.templates?.length || 0} → ${filtered.length}`);

      // Create deep copies to prevent reference sharing
      const templatesCopy = filtered.map(template => ({
        ...template,
        elements: template.elements ? JSON.parse(JSON.stringify(template.elements)) : [],
        pageSettings: template.pageSettings ? JSON.parse(JSON.stringify(template.pageSettings)) : null
      })) || [];
      return templatesCopy;
    } catch (error) {
      console.error('Failed to load templates by type:', error);
      return [];
    }
  }, []);

  // Load templates on component mount
  useEffect(() => {
    const loadAllTemplates = async () => {
      // If customElementsOnly mode, skip user-specific template loading
      if (!customElementsOnly) {
        // Use the same API as Brands page - load all templates without authentication issues
        await loadAllTemplatesForPopup();
      }

      // Load specific button subtype templates using type/subtype
      const [buttonImageCallout, buttonNotice, buttonText] = await Promise.all([
        loadTemplatesByTypeAndSubtype('Buttons', 'Button w/Image Call out'),
        loadTemplatesByTypeAndSubtype('Buttons', 'Button w/notice'),
        loadTemplatesByTypeAndSubtype('Buttons', 'Button w/text')
      ]);

      setButtonImageCalloutTemplates(buttonImageCallout);
      setButtonNoticeTemplates(buttonNotice);
      setButtonTextTemplates(buttonText);

      // If in customElementsOnly mode and Headline/Subheadline is selected, load those templates immediately
      if (customElementsOnly && selectedCustomCategory === 'Headline/Subheadline') {
        console.log('🔄 Loading Headline/Subheadline templates on mount...');
        const headlineTemplates = await loadTemplatesByType('Headline/Subheadline');
        console.log('📦 Loaded headline templates on mount:', headlineTemplates.map(t => ({ name: t.name, type: t.type, subtype: t.subtype })));
        setFilteredTemplates(headlineTemplates);
      }
    };

    loadAllTemplates();
  }, [loadAllTemplatesForPopup, loadTemplatesByTypeAndSubtype, customElementsOnly, selectedCustomCategory, loadTemplatesByType]);

  // Load templates when Load Template dialog opens
  useEffect(() => {
    if (showTemplatesList) {
      console.log('🔄 Load Template dialog opened, loading templates...');
      loadTemplates();
    }
  }, [showTemplatesList, loadTemplates]);

  // Load filtered templates when type and subtype change
  useEffect(() => {
    const loadFilteredTemplatesEffect = async () => {
      console.log('🔄 Loading filtered templates:', {
        selectedCustomCategory,
        selectedCustomSubtype: selectedCustomSubtype?.label
      });

      if (selectedCustomCategory && selectedCustomSubtype) {
        console.log(`🔍 Loading templates for ${selectedCustomCategory}/${selectedCustomSubtype.label}`);
        const templates = await loadTemplatesByType(selectedCustomCategory, selectedCustomSubtype.label);
        console.log(`📦 Received ${templates.length} templates for ${selectedCustomCategory}/${selectedCustomSubtype.label}:`, templates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype })));
        setFilteredTemplates(templates);
      } else if (selectedCustomCategory) {
        console.log(`🔍 Loading templates for ${selectedCustomCategory} only`);
        const templates = await loadTemplatesByType(selectedCustomCategory);
        console.log(`📦 Received ${templates.length} templates for ${selectedCustomCategory}:`, templates.map(t => ({ name: t.name, id: t._id, type: t.type, subtype: t.subtype })));
        setFilteredTemplates(templates);
      } else {
        console.log('🔍 No category selected, clearing templates');
        setFilteredTemplates([]);
      }
    };

    loadFilteredTemplatesEffect();
  }, [selectedCustomCategory, selectedCustomSubtype, loadTemplatesByType]);

  // Debug effect to monitor filteredTemplates changes
  useEffect(() => {
    console.log('🎯 filteredTemplates changed:', {
      selectedCustomCategory,
      selectedCustomSubtype: selectedCustomSubtype?.label,
      templatesCount: filteredTemplates.length,
      templates: filteredTemplates.map(t => ({ name: t.name, type: t.type, subtype: t.subtype }))
    });
  }, [filteredTemplates, selectedCustomCategory, selectedCustomSubtype]);

  // Clear subtype when template type changes
  useEffect(() => {
    if (templateType) {
      // Clear subtype when type changes to ensure compatibility
      setTemplateSubtype('');
    }
  }, [templateType]);

  // Force complete template state reset and reload
  const forceTemplateStateReset = useCallback(async () => {
    console.log('🔄 Forcing complete template state reset...');

    // Clear all template-related state
    setSavedTemplates([]);
    setPreBuiltTemplates([]);
    setButtonImageCalloutTemplates([]);
    setButtonNoticeTemplates([]);
    setButtonTextTemplates([]);
    setSelectedTemplateId(null);
    setSelectedVariantKey(null);

    // Wait a bit to ensure state is cleared
    await new Promise(resolve => setTimeout(resolve, 100));

    // Reload all templates fresh from database using unified API
    await loadAllTemplatesForPopup();

    // Load specific button subtype templates using type/subtype
    const [buttonImageCallout, buttonNotice, buttonText] = await Promise.all([
      loadTemplatesByTypeAndSubtype('Buttons', 'Button w/Image Call out'),
      loadTemplatesByTypeAndSubtype('Buttons', 'Button w/notice'),
      loadTemplatesByTypeAndSubtype('Buttons', 'Button w/text')
    ]);

    setButtonImageCalloutTemplates(buttonImageCallout);
    setButtonNoticeTemplates(buttonNotice);
    setButtonTextTemplates(buttonText);

    // Force re-render
    setForceRender(prev => prev + 1);

    console.log('✅ Template state reset completed');
  }, [loadAllTemplatesForPopup, loadTemplatesByTypeAndSubtype]);

  return {
    loadTemplates,
    saveTemplate,
    loadTemplate,
    deleteTemplate,
    createNewTemplate,
    loadPreBuiltTemplate,
    loadPreBuiltTemplates,
    loadAllTemplatesForPopup,
    loadTemplatesByTypeAndSubtype,
    loadTemplatesByType,
    forceTemplateStateReset,
    filterLegitimateTemplates,
    groupTemplatesByType,
  };
};
